<template>
  <div class="main-layout" style="position: relative">
    <MainHeader />
    <div class="main-container">
      <NavigationBar v-show="needShowNavigatorBar" class="main-mapp-navigator main-content" />
      <div :id="appContainerId" class="main-mapp-container main-content">
        <a-spin :spinning="defaultAppLoading" :indicator="indicator" :size="isStandardScreen ? 'large' : 'default'">
          <template #tip>
            <span class="spin-tip">{{ $t('owork_base.loading') }}</span>
          </template>
        </a-spin>
      </div>
    </div>
    <FloatNoticeStack class="float-notice-container" />
    <component :is="currentComponent" />
    <GuidePopperComponent />
    <component :is="currenWindowPopper" :binding-device-id="bindingDeviceId" />
  </div>
</template>
<script setup lang="ts">
  import { LoadingOutlined } from '@ant-design/icons-vue'
  import { computed, onMounted, ref, h, watch } from 'vue'
  import MainHeader from './MainHeader.vue'
  import NavigationBar from './NavigationBar.vue'
  import GuidePopperComponent from './GuidePopperComponent.vue'
  import FloatNoticeStack from '@components/notification/FloatNoticeStack.vue'
  import { useMicroAppContainer } from '@/core/compositions/MicroAppContainer'
  import { useQiankun } from '@/core/compositions/Qiankun'
  import InstallCertificate from '@/components/popwindows/InstallCertificate.vue'
  import { INSTANCE as WindowPopperManager } from '@/core/WindowPopperManager'
  import { INSTANCE as NotificationManager } from '@/core/Notification/NotificationManager'
  import type { NoticeDataType } from '@/core/Notification/NotificationManager'
  import { INSTANCE as DeviceManager } from '@core/devices/DeviceManager'
  import { INSTANCE as UserInfoManager } from '@/core/userInfo/UserInfoManager'
  import { useSharedActiveMenuItem } from '@/core/compositions/ActiveMenuItem'
  import { useSharedActiveMainHeaderItem, MainHeaderItems } from '@/core/compositions/ActiveMainHeaderItem'
  import NotificationCenter from '@components/notification/NotificationCenter.vue'
  import SettingsMainPage from '@components/settings/SettingsMainPage.vue'
  import { useSharedScreen } from '@/core/compositions/Screen'
  import { debounce } from 'lodash-es'
  import { TimeConstants } from '@/core/Constants'

  const { isStandardScreen } = useSharedScreen()
  const { currentSelectedItem } = useSharedActiveMainHeaderItem()
  const indicator = h(LoadingOutlined, {
    style: {
      fontSize: '26px',
      color: 'rgba(0, 0, 0, 0.9)',
    },
    spin: true,
  })
  const defaultAppLoading = ref<boolean>(true)

  const currentComponent = computed(() => {
    switch (currentSelectedItem.value) {
      case MainHeaderItems.NOTIFICATION_CENTER:
        return NotificationCenter
      case MainHeaderItems.SETTINGS_MAIN_PAGE:
        return SettingsMainPage

      default:
        return ''
    }
  })
  const { needShowNavigatorBar } = useSharedActiveMenuItem()
  const currenWindowPopper = ref<unknown>(undefined)
  const bindingDeviceId = ref<string | undefined>(undefined)

  WindowPopperManager.setChangeListener({
    onShow: (deviceId?: string) => {
      bindingDeviceId.value = deviceId
      currenWindowPopper.value = InstallCertificate
    },
    onClose: () => {
      currenWindowPopper.value = undefined
      bindingDeviceId.value = undefined
    },
  })

  const IS_MSG_SHOWN_INFO = '1'
  const { appContainerId } = useMicroAppContainer()

  useQiankun()

  const initNotice = async () => {
    const wlanMsgId = await addSysNotice('showWlanMsg', '文件浏览及传输过程中需消耗对应设备的流量，建议连接 WLAN')

    if (!wlanMsgId) {
      initFavoritesInfo()
    }
  }

  const initFavoritesInfo = async () => {
    // 临时注释进入页面时的提示收藏和添加设备通知，作为后续备用
    // addSysNotice('showFavoritesMsg', '建议将网址添加到浏览器收藏或书签，方便下次使用')
  }

  const addSysNotice = async (storeName: string, msg: string) => {
    try {
      const userInfo = await UserInfoManager.getUserInfo()

      if (!userInfo) return Promise.reject(undefined)

      const msgShownKey = `${storeName}${userInfo.sessionId}`
      const isMsgShowInfo = localStorage.getItem(msgShownKey)

      if (!isMsgShowInfo || isMsgShowInfo !== IS_MSG_SHOWN_INFO) {
        const { id: msgId } = NotificationManager.addSystemInfo({
          msg,
        })

        localStorage.setItem(msgShownKey, IS_MSG_SHOWN_INFO)

        return Promise.resolve(msgId)
      } else {
        return Promise.resolve(undefined)
      }
    } catch (error) {
      return Promise.reject(undefined)
    }
  }

  let hasShowNoDeviceNotice = false
  let notice: NoticeDataType | null = null
  const deviceListener = {
    onInitialized: () => {
      // do nothing
    },
    onDeviceListUpdated: () => {
      // 临时注释进入页面时的提示收藏和添加设备通知，作为后续备用
      // 设备列表为空时通知展示
      // if (DeviceManager.getDeviceList().length === 0) {
      //   if (!hasShowNoDeviceNotice) {
      //     notice = NotificationManager.addSystemInfo({
      //       msg: '未发现账号下存在任何设备',
      //       type: NoticeActionType.COMMON_ACTION,
      //       action: Action.GUIDE_DEVICE_ADDING,
      //       isEmphasized: true,
      //     })
      //     hasShowNoDeviceNotice = true
      //   }
      // } else {
      //   if (notice) NotificationManager.removeNotice([notice])
      //   notice = null
      // }
    },
    onCurrentSelectedDeviceChanged: () => {
      // do nothing
    },
  }

  NotificationManager.addNoticeListener({
    onAdded: () => {
      // do nothing
    },
    onUpdated: () => {
      // do nothing
    },
    onRemoved: () => {
      initFavoritesInfo()
    },
    onMarkAsRead() {
      initFavoritesInfo()
    },
    onRemoveAll() {
      // do nothing
    },
    onDismiss() {
      initFavoritesInfo()
    },
  })
  const appDom = ref<Element | null>()

  onMounted(() => {
    DeviceManager.addChangeListener(deviceListener)
    // initNotice()
    appDom.value = document.querySelector('#app')
    const onResize = debounce(() => {
      document.documentElement.style.setProperty('--real100vh', `${window.innerHeight}px`)
    }, TimeConstants.DEFAULT_DEBOUNCING_IN_MS)

    document.documentElement.style.setProperty('--real100vh', `${window.innerHeight}px`)
    window.addEventListener('resize', onResize)
  })
  const FULL_SCREEN_CLASS_NAME = 'full-screen'

  watch(
    isStandardScreen,
    (newValue) => {
      if (newValue) {
        appDom.value?.classList.remove(FULL_SCREEN_CLASS_NAME)
      } else {
        appDom.value?.classList.add(FULL_SCREEN_CLASS_NAME)
      }
    },
    {
      immediate: true,
    }
  )
</script>
<style lang="stylus" scoped>

  .main-container
    rect(100%, main_container_height)
    box-sizing border-box
    padding main_container_padding_top 0
    background-color rgb(240, 240, 240)
    display flex
    flex-direction column
    & > div
      width 100%

    .main-content
      width 95%
      max-width 1792px
      margin 0 auto
      min-width main_min_width * 0.95
    .main-mapp-navigator
      height navigator_height
      margin-bottom navigator_margin_bottom
      background-color #fff
      border-radius 12px
      display: flex
      justify-content: center
    .main-mapp-container
      flex 1
      background-color #fff
      border-radius 12px
      overflow hidden
  .float-notice-container
    position absolute
    z-index $zIndexToast
    right 16px
    top 81px
    width 288px
  .spin-tip
    color default_color
  :deep(.ant-spin)
    width 100%
    height 100%
    flex-all-center()
    flex-direction column
    .ant-spin-text
      margin-top 16px

  @import '../styles/h5Adaptive/MainContainer.styl'
</style>
